import { makeStyles } from "@material-ui/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import { Box, Button, Container, Icon, List, Paper, TextField, Typography } from "@mui/material";
import { deleteChatMessage, getChatMessage, sendChatMessage } from "api/routes";
import Loading from "components/Loading";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import moment from "jalali-moment";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";
import useMe from "../../hooks/useMe";

const useStyles = makeStyles({
  chatContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: "2rem",
  },
  chatPaper: {
    width: "100%",
    maxWidth: "80%",
    padding: "2rem",
    borderRadius: "15px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
  },
  messageInputContainer: {
    display: "flex",
    marginTop: "1rem",
    alignItems: "center",
  },
  messageInput: {
    flexGrow: 1,
  },
  sendButton: {
    marginLeft: "0.1rem",
    color: "white",
  },
  messageList: {
    maxHeight: "70vh",
    overflowY: "auto",
    marginTop: "1rem",
    padding: "0 1rem",
  },
  senderCard: {
    alignSelf: "flex-end",
    backgroundColor: "#c8e6c9",
    borderRadius: "10px",
    marginBottom: "0.5rem",
    padding: "8px 16px",
  },
  sender: {
    display: "flex",
    justifyContent: "flex-end",
  },
  receiver: {
    display: "flex",
  },
  receiverCard: {
    alignSelf: "flex-start",
    backgroundColor: "#bbdefb",
    borderRadius: "10px",
    marginBottom: "0.5rem",
    padding: "8px 16px",
  },
  cardContent: {
    padding: "8px 16px",
  },
});

export default function Chat() {
  const classes = useStyles();
  const file = useRef(null);

  const [messages, setMessages] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [user] = useState("aref-superadmin");
  const messagesEndRef = useRef(null);
  const [me] = useMe();

  function formatMessage(message) {
    return message.split("\n").map((item, index) => (
      <React.Fragment key={index}>
        {item}
        <br />
      </React.Fragment>
    ));
  }

  useEffect(() => {
    setLoading(true);
    getChatMessage((isOk, data) => {
      setLoading(false);
      if (!isOk) return toast.error(data.message);
      else {
        setMessages(data);
        scrollToBottom();
      }
    });
  }, []);

  const sendMessage = async () => {
    if (message) {
      const formData = new FormData();
      formData.append("message", message);
      if (uploadedFile)
        formData.append("attachment", uploadedFile);
      sendChatMessage(formData, (isOk, data) => {
        if (!isOk) return toast.error(data.message);
        else {
          setMessages(prev => ([...prev, data]));
          setMessage("");
          setUploadedFile(null);
          scrollToBottom();
        }
      });
    }
  };
  const deleteMessage = (chatId) => {
    deleteChatMessage(chatId, (isOk, data) => {
      if (!isOk) return toast.error(data.message);
      else {
        setMessages(prev => prev.filter(item => item.id !== chatId));
      }
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  const uploadImage = async (event) => {
    if (event.target.files && event.target.files.length > 0 && event.target.files[0] instanceof File) {
      if (event.target.files[0].size / 1024 / 1024 < 4) {
        const compressedImage = await imageCompression(event.target.files[0], { maxSizeMB: 1 });
        setUploadedFile(compressedImage);
      } else toast.error("حجم فایل باید کمتر از 4 مگابایت باشد");
    }
  };

  return (
    <DashboardLayout>
      {loading && <Loading />}
      <Container className={classes.chatContainer}>
        <Paper className={classes.chatPaper}>
          <Typography variant="h4" gutterBottom>پشتیبانی</Typography>
          <List className={classes.messageList}>
            {messages.map((msg, index) => (
              <Box key={index} className={msg.username === me.username ? classes.receiver : classes.sender}>
                <div className={msg.username === user ? classes.senderCard : classes.receiverCard}>
                  <Typography variant="subtitle2" gutterBottom
                              style={{ color: msg.username === user ? "#008a05" : "#0089ff" }}>{msg.username === user ? "پشتیبان" : msg.username}</Typography>

                  {msg.attachment &&
                    <img src={`data:image/jpeg;base64,${msg.attachment}`} alt={"attachment"} style={{ height: 150 }} />}
                  <Typography variant="h6"
                              style={{ fontWeight: "normal" }}>{formatMessage(msg.message)}</Typography>
                  <div style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                  }}>
                    <Typography fontSize={10}
                                style={{ marginTop: 5 }}>{moment(msg.createdAt, "jYYYY/jMM/jDD HH:mm:ss").locale("fa").fromNow()}</Typography>
                    {msg.username === me.username &&
                      <Icon style={{ color: "red",cursor:"pointer" }} onClick={() => deleteMessage(msg.id)}
                            fontSize={"small"}>delete</Icon>}
                  </div>
                </div>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </List>
          <Box className={classes.messageInputContainer}>
            <Button
              variant="contained"
              color="primary"
              onClick={sendMessage}
              className={classes.sendButton}
            >
              <SendIcon />
            </Button>
            <TextField
              label="Type a message"
              variant="outlined"
              fullWidth
              sx={{
                ".MuiOutlinedInput-root": {
                  paddingLeft: "1rem",
                  flexDirection: "column",
                },
                img: {
                  paddingRight: "1rem",
                },
              }}
              InputProps={{
                startAdornment: uploadedFile &&
                  <img src={URL.createObjectURL(uploadedFile)} alt={"attachment"} style={{ height: 150 }} />,
              }}
              multiline
              value={message}
              onChange={e => setMessage(e.target.value)}
              className={classes.messageInput}
            />
            <AttachFileIcon onClick={() => file?.current?.click()}
                            style={{ width: 32, cursor: "pointer", transform: "rotate(40deg)" }} />
          </Box>
        </Paper>
      </Container>
      <input ref={file} type="file" accept="image/*" style={{ display: "none" }} onChange={uploadImage} />
    </DashboardLayout>
  );
}
